import { Fragment, useState } from "react"
import Header from "components/Layouts/Header"
import ImageUploader from "../ImageUploader"
import { useAppSelector } from "app/hooks"
import { useRevertFoodScanToOriginalImage } from "features/meal/hooks/useScannedImageUploader"
import AppLoading from "components/loading/AppLoading"
import PortionWithUnitTextField from "components/portion/PortionWithUnitTextField"
import { FormItemLabel, MealFoodContent } from "./styled"
import Button from "components/Buttons/Button"
import useScannedMealFoodForm from "features/food/hooks/scanResultFoods/useAIScanFoodForm"
import TextAreaFieldFormItem from "components/inputs/TextAreaFieldFormItem"
import Ingredients from "./Ingredients"
import SearchIngredients from "features/food/CreateMyFood/SearchIngredients"
import AIIngredientDetails from "./AIIngredientDetails"
import AIComment from "../AIComment"
import PopConfirm from "components/PopConfirm"
import useGramFromIngredients from "features/food/hooks/useGramFromIngredients"
import EdamaFoodDetails from "components/foodDetails/EdamaFoodDetails"
import { AIScanFoodOptionType, IngredientOptionType } from "model/food/types"
import {
  IAIScanFoodOption,
  IAIScanIngredientOption,
} from "model/food/types/AIScan.types"
import { isEdamaFood } from "model/food/utils/typeGuards/foodTypeGuards"
import AppStyleCtaBox from "components/Common/AppStyleCtaBox"
import EditableNutrientInformation from "components/foodDetails/share/EditableNutrientInformation"
import EditNutrientForm from "components/foodDetails/share/EditNutrientForm"

interface IMealFoodFormProps {
  food: IAIScanFoodOption
  onUpdateFood: (updatedFood: IAIScanFoodOption) => void
  onBack: () => void
}

enum IMealFoodFormSection {
  Main = "Main",
  SearchIngredients = "SearchIngredients",
  IngredientDetails = "IngredientDetails",
  EditNutrientForm = "EditNutrientForm",
}

const AIScanFoodDetails = (props: IMealFoodFormProps) => {
  const [section, setSection] = useState(IMealFoodFormSection.Main)
  const { food: initFood, onBack, onUpdateFood } = props
  const onRevertFoodScanToOriginalImage = useRevertFoodScanToOriginalImage(
    initFood.id,
  )

  const processScannedFoodImageLoading = useAppSelector(
    (state) => state.meal.processScannedFoodImageLoading,
  )

  const [isPortionValid, setIsPortionValid] = useState(true)
  const [isHideSearchHeader, setIsHideSearchHeader] = useState(false)

  const [ingredientDetails, setIngredientDetails] = useState<
    undefined | IngredientOptionType
  >()

  const {
    food,
    onNameChange,
    onPortionChange,
    onUnitChange,
    onAddIngredient,
    onDeleteIngredient,
    onUpdateIngredient,
    isFoodChanged,
    updateFoodAsManualNutrition,
  } = useScannedMealFoodForm(initFood)
  const gram = useGramFromIngredients(food)

  const [isPopConfirmDiscardOpen, setIsPopConfirmDiscardOpen] = useState(false)

  const handleUpdateFood = () => {
    const updatedFood: AIScanFoodOptionType = {
      ...initFood,
      ...food,
    }
    onUpdateFood(updatedFood)
  }

  const showSearchIngredients = () => {
    setSection(IMealFoodFormSection.SearchIngredients)
  }

  const handleBack = () => {
    onRevertFoodScanToOriginalImage()
    onBack()
  }

  if (section === IMealFoodFormSection.EditNutrientForm) {
    return (
      <EditNutrientForm
        onClose={() => {
          setSection(IMealFoodFormSection.Main)
        }}
        meal={food}
        onSubmit={updateFoodAsManualNutrition}
      />
    )
  }

  if (section === IMealFoodFormSection.Main) {
    return (
      <Fragment>
        <PopConfirm
          isOpen={isPopConfirmDiscardOpen}
          title="Unsaved changes?"
          cancelText="Discard"
          confirmText="Save changes"
          onCancel={handleBack}
          onConfirm={handleUpdateFood}
          loading={false}
        >
          <p style={{ textAlign: "center" }}>
            You haven't saved your meal yet. Are you sure you want to leave?
          </p>
        </PopConfirm>

        <Header
          isAppPaddingStyle
          disabled={processScannedFoodImageLoading}
          title=""
          onBack={() => {
            if (isFoodChanged()) {
              setIsPopConfirmDiscardOpen(true)
            } else if (!processScannedFoodImageLoading) {
              handleBack()
            }
          }}
        />

        <MealFoodContent>
          <TextAreaFieldFormItem
            label="Name"
            placeholder="Name your meal"
            value={food.name}
            onChange={onNameChange}
          />

          <AIComment comment={initFood.comment} />

          <PortionWithUnitTextField
            portionValue={food.portion}
            unitValue={food.unit}
            onUnitChange={onUnitChange}
            onPortionChange={onPortionChange}
            setIsPortionValid={setIsPortionValid}
            calories={food.calorie}
            gram={gram}
          />

          <EditableNutrientInformation
            entry={food}
            setEditMode={() => {
              setSection(IMealFoodFormSection.EditNutrientForm)
            }}
          />

          <Ingredients
            showSearchIngredients={showSearchIngredients}
            ingredients={food.ingredients}
            onRemoveIngredient={onDeleteIngredient}
            onViewIngredientDetails={(ingredient) => {
              setSection(IMealFoodFormSection.IngredientDetails)
              setIngredientDetails(ingredient)
            }}
            isManualNutrition={food.isManualNutrition}
          />

          <div>
            <FormItemLabel>
              Image <span>{"(optional)"}</span>
            </FormItemLabel>
            <ImageUploader foodId={initFood.id} />
          </div>
        </MealFoodContent>

        <AppStyleCtaBox>
          <Button
            disabled={
              !isPortionValid ||
              processScannedFoodImageLoading ||
              !food.ingredients.length
            }
            $type="primary"
            onClick={handleUpdateFood}
            style={{ width: "100%" }}
          >
            Save
          </Button>
        </AppStyleCtaBox>

        {processScannedFoodImageLoading && (
          <AppLoading content="Processing image" />
        )}
      </Fragment>
    )
  }

  if (section === IMealFoodFormSection.SearchIngredients) {
    return (
      <Fragment>
        {!isHideSearchHeader && (
          <Header
            isAppPaddingStyle
            disabled={processScannedFoodImageLoading}
            title="Add Ingredients"
            onBack={() => {
              setSection(IMealFoodFormSection.Main)
            }}
          />
        )}

        <SearchIngredients
          onAddIngredient={(ingredient) => {
            onAddIngredient(ingredient)
          }}
          setIsHideHeader={(isHidden) => {
            setIsHideSearchHeader(isHidden)
          }}
        />
      </Fragment>
    )
  }

  if (!ingredientDetails) {
    return null
  }

  return isEdamaFood(ingredientDetails) ? (
    <EdamaFoodDetails
      isIngredient
      onBack={() => {
        setSection(IMealFoodFormSection.Main)
      }}
      food={ingredientDetails}
      mode="edit"
      onEdit={(food) => {
        onUpdateIngredient(food)
      }}
      onDelete={() => {
        onDeleteIngredient(ingredientDetails.uuid)
        setSection(IMealFoodFormSection.Main)
      }}
    />
  ) : (
    <AIIngredientDetails
      ingredient={ingredientDetails as IAIScanIngredientOption}
      onBack={() => setSection(IMealFoodFormSection.Main)}
      onEdit={onUpdateIngredient}
      onDelete={(id) => {
        onDeleteIngredient(id)
        setSection(IMealFoodFormSection.Main)
      }}
    />
  )
}

export default AIScanFoodDetails
